import type {
    PickerItemData,
    PickerItemValue,
    PickerNested,
    PickerSectionData,
    PickerTreePosition,
} from "./Picker.types";

/** One rendered row of a flattened item tree. */
export interface PickerTreeRow<T extends PickerItemValue> {
    item: PickerItemData<T>;
    depth: number;
    treePosition: PickerTreePosition;
    /** Depths (1-based) of ancestor sibling groups whose connector runs through this row. */
    ancestorLines: readonly number[];
    isParent: boolean;
    isExpanded: boolean;
    isSelectable: boolean;
    /** The row opens its children in a flyout level instead of expanding in place. */
    isFlyout: boolean;
    isMatch: boolean;
}

export interface FlattenItemsOptions<T extends PickerItemValue> {
    items: ReadonlyArray<PickerItemData<T>>;
    expandedValues: ReadonlySet<T>;
    collapsedValues?: ReadonlySet<T>;
    /** Every tree parent not in `collapsedValues` starts expanded (`defaultExpandAll`). */
    isExpandedByDefault?: boolean;
    /** Tree parents not in `collapsedValues` that start expanded (`defaultExpandedValues`). */
    defaultExpandedValues?: ReadonlySet<T>;
    query: string;
    selectedValues: readonly T[];
    selectableParents?: boolean;
    /** Flyout presentation never expands a parent in place; its children live in the level. */
    nested?: PickerNested;
    /** Replaces the default case-insensitive label match; receives the raw query. */
    isMatch?: PickerItemMatcher<T>;
}

/** Decides whether an item matches a non-empty search query. */
export type PickerItemMatcher<T extends PickerItemValue> = (
    item: PickerItemData<T>,
    query: string,
) => boolean;

const normalizeQuery = (query: string) => query.trim().toLowerCase();

/** The default matcher: the query appears in the label or the label hint. */
export const isLabelMatch = <T extends PickerItemValue>(
    item: PickerItemData<T>,
    query: string,
) => {
    const needle = normalizeQuery(query);
    return [item.label, item.labelHint].some(
        (text) => text?.toLowerCase().includes(needle) === true,
    );
};

/** The rows nested under an item: its bare `children`, or every item of its `sections`. */
export const levelItems = <T extends PickerItemValue>(
    item: PickerItemData<T>,
): ReadonlyArray<PickerItemData<T>> =>
    item.sections !== undefined
        ? item.sections.flatMap((section) => section.items)
        : (item.children ?? []);

/** Composite search: whether any row nested under the item matches the query. */
export const hasMatchingDescendant = <T extends PickerItemValue>(
    item: PickerItemData<T>,
    query: string,
    isMatch: PickerItemMatcher<T> = isLabelMatch,
): boolean =>
    levelItems(item).some(
        (child) =>
            isMatch(child, query) ||
            hasMatchingDescendant(child, query, isMatch),
    );

/** Whether any row nested under the item (through sections too) is one of the values. */
export const containsSelectedValue = <T extends PickerItemValue>(
    item: PickerItemData<T>,
    selectedValues: ReadonlySet<T>,
): boolean =>
    levelItems(item).some(
        (child) =>
            selectedValues.has(child.value) ||
            containsSelectedValue(child, selectedValues),
    );

/** A row's connector shape from its index among `siblingCount` visible siblings. */
export const treePositionAt = (
    index: number,
    siblingCount: number,
): PickerTreePosition => {
    if (siblingCount === 1) {
        return "only";
    }
    if (index === 0) {
        return "top";
    }
    if (index === siblingCount - 1) {
        return "bottom";
    }
    return "middle";
};

/**
 * Flattens nested items into the rows the list renders. Parents are collapsed
 * unless expanded explicitly, expanded by default or holding a selected
 * value; an explicit collapse wins over both. A non-empty query
 * keeps a parent when any descendant matches, forces it open, and a matching
 * parent shows all of its children.
 */
export const flattenItems = <T extends PickerItemValue>({
    items,
    expandedValues,
    collapsedValues,
    isExpandedByDefault = false,
    defaultExpandedValues,
    query,
    selectedValues,
    selectableParents = false,
    nested = "tree",
    isMatch = isLabelMatch,
}: FlattenItemsOptions<T>): Array<PickerTreeRow<T>> => {
    const hasQuery = normalizeQuery(query) !== "";
    const isFlyoutNested = nested === "flyout";
    const selectedSet = new Set(selectedValues);
    const rows: Array<PickerTreeRow<T>> = [];

    const visit = ({
        siblings,
        depth,
        isAncestorMatch,
        ancestorLines,
    }: {
        siblings: ReadonlyArray<PickerItemData<T>>;
        depth: number;
        isAncestorMatch: boolean;
        ancestorLines: readonly number[];
    }) => {
        const visibleSiblings = siblings.filter(
            (item) =>
                !hasQuery ||
                isAncestorMatch ||
                isMatch(item, query) ||
                hasMatchingDescendant(item, query, isMatch),
        );

        visibleSiblings.forEach((item, index) => {
            // Only a flyout level can be sectioned; in a tree a sectioned
            // item has nothing to expand in place.
            const isParent =
                item.sections !== undefined
                    ? isFlyoutNested && levelItems(item).length > 0
                    : levelItems(item).length > 0;
            const isItemMatch = hasQuery && isMatch(item, query);
            // A flyout parent never opens in place; its level holds the children.
            let isExpanded = false;

            if (isParent && !isFlyoutNested && hasQuery) {
                isExpanded =
                    isAncestorMatch ||
                    isItemMatch ||
                    hasMatchingDescendant(item, query, isMatch);
            } else if (
                isParent &&
                !isFlyoutNested &&
                !collapsedValues?.has(item.value)
            ) {
                isExpanded =
                    isExpandedByDefault ||
                    defaultExpandedValues?.has(item.value) === true ||
                    expandedValues.has(item.value) ||
                    containsSelectedValue(item, selectedSet);
            }

            const treePosition = treePositionAt(index, visibleSiblings.length);
            rows.push({
                item,
                depth,
                treePosition,
                ancestorLines,
                isParent,
                isExpanded,
                isSelectable: !isParent || selectableParents,
                isFlyout: isParent && isFlyoutNested,
                isMatch: isItemMatch,
            });

            if (isParent && isExpanded) {
                // A sibling group's line keeps running through the children of
                // a non-last member, so the stroke reaches the next sibling.
                const isLineContinuing =
                    depth > 0 &&
                    (treePosition === "top" || treePosition === "middle");
                visit({
                    siblings: levelItems(item),
                    depth: depth + 1,
                    isAncestorMatch: isAncestorMatch || isItemMatch,
                    ancestorLines: isLineContinuing
                        ? [...ancestorLines, depth]
                        : ancestorLines,
                });
            }
        });
    };

    visit({
        siblings: items,
        depth: 0,
        isAncestorMatch: false,
        ancestorLines: [],
    });

    return rows;
};

/** Rows of one data-driven group: the whole `items` list, or one `sections` entry. */
export interface PickerRowGroup<T extends PickerItemValue> {
    key: string;
    section?: PickerSectionData<T>;
    rows: Array<PickerTreeRow<T>>;
}

export interface BuildRowGroupsOptions<T extends PickerItemValue> extends Omit<
    FlattenItemsOptions<T>,
    "items"
> {
    items?: ReadonlyArray<PickerItemData<T>>;
    sections?: ReadonlyArray<PickerSectionData<T>>;
}

/** Flattens `items` into one group, or every `sections` entry into its own group. */
export const buildRowGroups = <T extends PickerItemValue>({
    items,
    sections,
    ...flattenOptions
}: BuildRowGroupsOptions<T>): Array<PickerRowGroup<T>> => {
    if (sections !== undefined) {
        return sections.map((section, index) => ({
            key: section.key ?? section.title ?? String(index),
            section,
            rows: flattenItems({ ...flattenOptions, items: section.items }),
        }));
    }
    return [
        {
            key: "items",
            rows: flattenItems({ ...flattenOptions, items: items ?? [] }),
        },
    ];
};

/** Every item at every depth, in document order. */
export const collectItems = <T extends PickerItemValue>(
    items: ReadonlyArray<PickerItemData<T>>,
): Array<PickerItemData<T>> =>
    items.flatMap((item): Array<PickerItemData<T>> => [
        item,
        ...collectItems(levelItems(item)),
    ]);

/** Values a section select-all covers: every descendant, parents only when selectable. */
export const collectSelectableValues = <T extends PickerItemValue>({
    items,
    selectableParents,
}: {
    items: ReadonlyArray<PickerItemData<T>>;
    selectableParents: boolean;
}): T[] =>
    collectItems(items)
        .filter((item) => selectableParents || levelItems(item).length === 0)
        .map((item) => item.value);

/**
 * Cascade map of a data-driven list: every parent (at any depth, through
 * flyout levels and their sections) to the enabled leaf values below it, the
 * set its checkbox toggles under `parentSelection="cascade"`.
 */
export const collectCascadeDescendants = <T extends PickerItemValue>(
    items: ReadonlyArray<PickerItemData<T>>,
): ReadonlyMap<T, readonly T[]> => {
    const map = new Map<T, readonly T[]>();
    const visit = (item: PickerItemData<T>): T[] => {
        const children = levelItems(item);
        if (children.length === 0) {
            return item.disabled ? [] : [item.value];
        }
        const leaves = children.flatMap(visit);
        map.set(item.value, leaves);
        return leaves;
    };
    items.forEach(visit);
    return map;
};

/**
 * Every parent (at any depth, through flyout levels and their sections) to
 * every value below it, parents and disabled rows included: the rows whose
 * selection makes a `self` parent read indeterminate.
 */
export const collectDescendantValues = <T extends PickerItemValue>(
    items: ReadonlyArray<PickerItemData<T>>,
): ReadonlyMap<T, readonly T[]> => {
    const map = new Map<T, readonly T[]>();
    const visit = (item: PickerItemData<T>): T[] => {
        const children = levelItems(item);
        if (children.length === 0) {
            return [item.value];
        }
        const descendants = children.flatMap(visit);
        map.set(item.value, descendants);
        return [item.value, ...descendants];
    };
    items.forEach(visit);
    return map;
};
